/**
 * `setlist.module.css` — the SET LIST sheet. `docs/design-direction.md` §8.4.
 *
 * The set list is the selector's working document, and it is the **one deliberately
 * light screen** in the app: a cream `--stock` sheet with 1px `--cool-ink` ruling at
 * 18%, hand numbering down the left margin, black UI type, tape at the top corners and
 * hard-cut edges. Every one of those comes from the shared `.paper` recipe in
 * `styles/texture.css` by way of the `Paper` primitive — this file adds the ledger that
 * sits **on** the ruling and the four interactions §8.4 specifies, and nothing else.
 *
 * Five things in here are load-bearing and must not be "cleaned up":
 *
 *  1. **Rows are 56px** (`--drop` + `--s3`), which is exactly two 28px ruling steps, so
 *     every row lands on the printed line instead of drifting off it. Change the row
 *     height and the sheet stops looking ruled.
 *  2. **Separation is the ruling, not a border.** There is no `border-block-end` on a
 *     row: the paper is already ruled, and a second line per row would print twice.
 *  3. **Reorder lifts 2px with `--ink-offset`** — a hard letterpress offset with zero
 *     blur. No scale, no blur, no tilt (§8.4, §5.5.4). The drop target is a 3px
 *     `--accent` bar drawn on the row edge, which is the same mark as the downbeat.
 *  4. **Swipe-left strikes, it does not delete** (§8.4). The struck row keeps its grid
 *     slot, greys to a mix of the paper ink, draws a 1px strike from the left over
 *     `--t16`, and swaps its drag handle for a mono `ОТМЕНИТЬ` control. Nothing is
 *     removed until the undo window closes.
 *  5. **Blend-mode budget** (§6.13): this screen's tree carries exactly three blended
 *     elements — the two `paper__tape` strips and the now-playing `<mark>`. Nothing
 *     here may add a fourth; the grain layer is the Shell's, not the screen's.
 *
 * Colour on paper: components reference the semantic aliases only (§3.2), so every
 * value below resolves from `--text-on-paper`, `--bg-paper`, `--accent`, `--border` or
 * `--focus`. The greys are `color-mix()` over those aliases rather than a new token,
 * because a "text-3 on paper" token would be a fifth text colour nobody else needs.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   The sheet shell. `Sheet` (§9.8) supplies the dialog, the halftone scrim, the focus
   trap, Escape, the BackButton and the rise; the object that rises is the cream sheet
   inside it, so the panel itself is transparent. The two cannot be merged: `.sheet`
   animates `transform` for the rise and the dismiss drag, and `.paper` *is* a
   `transform` (the −0.8° rotation). One element cannot carry both.
   ──────────────────────────────────────────────────────────────────────────────── */

.sheet {
  background: none;
}

/*
 * THE PAPER IS THE SCROLLER, NOT THE PANEL. This is load-bearing and cost a bug.
 *
 * `.paper`'s hard-cut edge is a `clip-path` (§6.11), and a `clip-path` clips *painting*
 * only: it does not clip scrollable overflow and it does not make a scroll container.
 * So while `Sheet`'s own `.body` did the scrolling, a set list longer than the sheet
 * behaved like this — the ledger laid itself out full height (30 rows ≈ 1680px), the
 * paper box stayed capped at 80% of `--app-h`, and every row past the cap was painted
 * nowhere. Scrolling then dragged the whole clipped paper up off the top edge and left
 * an empty panel underneath it: "лист куда-то уезжает" (operator screenshot,
 * 2026-09-05).
 *
 * The fix takes the scroll off the panel and gives it to the paper. `.body` becomes a
 * single-row grid that cannot scroll, the wrapper passes that row height straight
 * through, and the paper — now a stretched grid item — is a fixed-size window with the
 * ledger scrolling inside it. The ruling is a background on the paper, so it scrolls
 * with the rows and every row still lands on a printed line.
 *
 * This also keeps §8.4's single documented exception to the inversion cap honest: the
 * cream block never exceeds 80% of the viewport because it can no longer be taller than
 * the sheet that holds it. Measured on a 390×844 phone: 646px, 76.5% of `--app-h`.
 * `--app-h` comes from Telegram's `viewportStableHeight`, never a viewport unit
 * (§10.1, §12.18).
 *
 * Handle: `apps/web/src/screens/__tests__/setlist-scroll.test.tsx`.
 */
.body {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
}

/* Beats `primitives.module.css`'s single-class `.body` regardless of bundle order. */
.sheet .body {
  overflow: hidden;
}

/* The `data-playing` wrapper is furniture: it passes the row height through. */
.body > * {
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  min-block-size: 0;
}

/*
 * `.sheet .paper`, not `.paper`, and the extra class is load-bearing. `texture.css`'s
 * global `.paper` recipe opens with the `background` SHORTHAND, which resets
 * `background-attachment` to `scroll` — and it is emitted after this module in the
 * bundle (measured: offset 61346 vs 44645 in `dist/assets/index.*.css`), so at equal
 * specificity it won and the `local` below was dead CSS that computed as `scroll`.
 * 0,2,0 settles it without depending on bundle order at all.
 */
.sheet .paper {
  min-block-size: 0;
  overflow-y: auto;

  /*
   * `local`, and this is rule 1 of this file (rows land on the printed line). The
   * ruling is a `background-image` on this element, and the default `scroll` pins a
   * background to the element's own box while its contents move — scroll 300px and
   * every row sits 8px off its line. `local` scrolls the ruling with the rows, so the
   * 56px row keeps its two 28px steps at every offset. Measured: line phase 17.4px at
   * scrollTop 0, 300 and 317 with `local`; 17.4 → 25.4 with `scroll`.
   *
   * The two tape strips are children, so they travel with the sheet as the ruling
   * does — the printed sheet slides under the window, tape and all.
   */
  background-attachment: local;

  /* The set list must not chain its scroll into Telegram's own sheet dismiss (§7.3). */
  overscroll-behavior: contain;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The CLEAR stamp — a word in a square outline, never an icon-only trash can (§12.21).
   ──────────────────────────────────────────────────────────────────────────────── */

.top {
  display: flex;
  justify-content: flex-end;
  margin-block-end: var(--s2);
}

.clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-block-size: var(--drop);
  min-inline-size: var(--drop);
  padding-inline: var(--s3);
  border: 1px solid var(--text-on-paper);
  border-radius: var(--r-cut);
  background: none;
  color: var(--text-on-paper);
}

.clear:active {
  background: color-mix(in srgb, var(--text-on-paper) 12%, transparent);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The ledger. `grid-template-columns: 3ch 1fr auto 44px` →
   [hand number][title + performer][duration][grab area].
   ──────────────────────────────────────────────────────────────────────────────── */

.list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.row {
  position: relative;
  display: grid;
  grid-template-columns: 3ch 1fr auto var(--drop);
  align-items: center;
  gap: var(--s2);

  /* 44 + 12 = 56px = two 28px ruling steps. */
  min-block-size: calc(var(--drop) + var(--s3));
  touch-action: pan-y;
}

/* The hand numbering. Sized here, `--ff-scrawl` comes from the global `.scrawl` class
   so `[data-scrawl="off"]` can replace handwriting with what a plant would have
   typeset instead (§4.4, §12.15) — never fake it with a script fallback. */
.num {
  font-size: var(--fs-20);
  color: color-mix(in srgb, var(--text-on-paper) 70%, transparent);
  text-align: end;
  transform: rotate(-3deg);
}

.main {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--s3);
  min-block-size: var(--drop);
  padding: 0;
  border: 0;
  border-radius: var(--r-cut);
  background: none;
  color: inherit;
  text-align: start;
  touch-action: pan-y;
}

.main:active {
  background: color-mix(in srgb, var(--text-on-paper) 8%, transparent);
}

.lines {
  position: relative;
  display: block;
  min-inline-size: 0;
}

.title {
  display: block;
  color: var(--text-on-paper);
}

.meta {
  display: block;
  color: color-mix(in srgb, var(--text-on-paper) 62%, transparent);
}

.duration {
  color: color-mix(in srgb, var(--text-on-paper) 78%, transparent);
}

/* The now-playing item's crooked felt-tip swipe. `mark` carries the whole recipe —
   `--accent` at 55% on `multiply` with a hand-cut `clip-path` (§6.11) — so this only
   restores the ink the button would otherwise inherit. */
.marker {
  color: var(--text-on-paper);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The grab area: three 12px chalk dashes inside a 44px target (§8.4).
   ──────────────────────────────────────────────────────────────────────────────── */

.handle {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--s1);
  inline-size: var(--drop);
  min-block-size: var(--drop);
  padding: 0;
  border: 0;
  border-radius: var(--r-cut);
  background: none;

  /* The drag must not be stolen by the scroller or by Telegram's sheet dismiss. */
  touch-action: none;
}

.dash {
  display: block;
  inline-size: var(--s3);
  block-size: 1px;
  background: color-mix(in srgb, var(--text-on-paper) 55%, transparent);
}

.handle:active .dash {
  background: var(--text-on-paper);
}

/* REORDER LIFT (§8.4): 2px and a hard ink offset. No scale, no blur, no tilt. */
.row[data-dragging='true'] {
  z-index: 1;
  transform: translateY(-2px);
  box-shadow: var(--ink-offset);
  background: var(--bg-paper);
}

/* The drop indicator is the downbeat mark: a 3px `--accent` bar on the row edge. */
.row[data-drop]::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  block-size: 3px;
  background: var(--accent);
  pointer-events: none;
}

.row[data-drop='before']::after {
  inset-block-start: -1px;
}

.row[data-drop='after']::after {
  inset-block-end: -1px;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The strike. Swipe-left crosses the tune off the list; it never deletes (§8.4).
   ──────────────────────────────────────────────────────────────────────────────── */

.struck .title,
.struck .meta,
.struck .duration,
.struck .num {
  color: color-mix(in srgb, var(--text-on-paper) 42%, transparent);
}

.struck .lines::after {
  content: '';
  position: absolute;
  inset-inline: 0;
  inset-block-start: 45%;
  block-size: 1px;
  background: currentcolor;
  transform-origin: left center;
  animation: strike var(--t16) var(--ease-cut) 1 both;
  pointer-events: none;
}

@keyframes strike {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

/* The undo control replaces the drag handle for the length of the window: a struck row
   is not reorderable, and two controls in one 44px slot is a mis-tap waiting to
   happen. */
.undo {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-block-size: var(--drop);
  padding-inline: var(--s2);
  border: 1px solid var(--accent);
  border-radius: var(--r-cut);
  background: none;
  color: var(--text-on-paper);
  white-space: nowrap;
}

.undo:active {
  background: color-mix(in srgb, var(--accent) 22%, transparent);
}

/* ────────────────────────────────────────────────────────────────────────────────
   PLAYED — the history below the second downbeat rule, collapsed to five (§8.4).
   ──────────────────────────────────────────────────────────────────────────────── */

.played {
  margin-block-start: var(--s6);
}

.played .row {
  grid-template-columns: 3ch 1fr auto;
}

.played .title,
.played .duration {
  color: color-mix(in srgb, var(--text-on-paper) 50%, transparent);
}

.more {
  display: inline-flex;
  align-items: center;
  min-block-size: var(--drop);
  padding-inline: var(--s2);
  border: 0;
  border-radius: var(--r-cut);
  background: none;
  color: color-mix(in srgb, var(--text-on-paper) 70%, transparent);
}

/* ────────────────────────────────────────────────────────────────────────────────
   Focus and Cyrillic, both system-wide rules restated for the paper ground.
   ──────────────────────────────────────────────────────────────────────────────── */

.paper :focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: var(--r-cut);
}

/* Tracked uppercase Cyrillic reads as shouting and is banned in the label tier
   (§4.3, §12.16): the mono tier survives, the tracking and the forced case do not. */
:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}
